import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  type RunpodEndpointStatus,
  WorkerProviderService,
  type WorkerProviderSummary,
} from '../../services/worker-provider.service';
import {
  WorkerExecutionPreferenceService,
  type WorkerExecutionPreference,
} from '../../services/worker-execution-preference.service';
import { MetabaseReportService } from '../../services/metabase-report.service';

@Component({
  selector: 'app-gpu-worker-dialog',
  imports: [],
  templateUrl: './gpu-worker-dialog.html',
  styleUrl: './gpu-worker-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.aria-hidden]': '!open()',
  },
})
export class GpuWorkerDialog {
  private readonly workerProviders = inject(WorkerProviderService);
  private readonly executionPreferences = inject(WorkerExecutionPreferenceService);
  private readonly metabaseReports = inject(MetabaseReportService);

  readonly open = input(false);
  readonly closed = output<void>();

  readonly executionPreference = signal<WorkerExecutionPreference>(this.executionPreferences.getExecutionPreference());
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly lastRefreshedLabel = signal<string | null>(null);
  readonly providers = signal<WorkerProviderSummary[]>([]);
  readonly runpodEndpointStatuses = signal<RunpodEndpointStatus[]>([]);

  readonly runpodProvider = computed(
    () => this.providers().find((provider) => provider.id === 'runpod') ?? null,
  );

  readonly jobTimingHistoryUrl = computed(() => {
    return this.metabaseReports.jobTimingHistoryUrl();
  });

  readonly isGpuMode = computed(() => this.executionPreference() === 'runpod');

  constructor() {
    effect(() => {
      if (!this.open()) {
        return;
      }
      void this.refresh();
    });
  }

  async refresh(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    this.runpodEndpointStatuses.set([]);

    try {
      const providers = await firstValueFrom(this.workerProviders.getProviders());
      this.providers.set(providers);
      this.lastRefreshedLabel.set(this.formatNowForDisplay());

      if (!this.isGpuMode()) {
        return;
      }

      const runpodProvider = providers.find((provider) => provider.id === 'runpod');
      if (!runpodProvider?.configured) {
        return;
      }

      const endpointStatuses = await firstValueFrom(this.workerProviders.getRunpodEndpointStatuses());
      this.runpodEndpointStatuses.set(endpointStatuses);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load GPU worker offerings.';
      this.loadError.set(message);
    } finally {
      this.loading.set(false);
    }
  }

  setExecutionPreference(value: WorkerExecutionPreference): void {
    if (this.executionPreference() === value) {
      return;
    }
    this.executionPreference.set(value);
    this.executionPreferences.setExecutionPreference(value);
    if (this.open()) {
      void this.refresh();
    }
  }

  close(): void {
    if (this.loading()) {
      return;
    }
    this.closed.emit();
  }

  healthStatusLabel(status: RunpodEndpointStatus['healthStatus']): string {
    if (status === 'healthy') {
      return 'Healthy';
    }
    if (status === 'degraded') {
      return 'Degraded';
    }
    return 'Offline';
  }

  gpuLabel(endpoint: RunpodEndpointStatus): string {
    if (endpoint.gpuIds.length > 0) {
      return endpoint.gpuIds.join(' | ');
    }
    if (endpoint.gpuCount > 0) {
      return `Pool-managed (${endpoint.gpuCount} GPU per worker)`;
    }
    return 'Pool-managed (GPU type not exposed)';
  }

  workerPoolLabel(endpoint: RunpodEndpointStatus): string {
    const min = endpoint.workersMin;
    const max = endpoint.workersMax;
    if (min == null && max == null) {
      return 'Not configured';
    }
    if (min == null) {
      return `Max ${max}`;
    }
    if (max == null) {
      return `Min ${min}`;
    }
    return `${min} - ${max}`;
  }

  private formatNowForDisplay(): string {
    return new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }
}